import { Grid } from '@mui/material';
import { CustomFields as CustomFieldsValueObject } from '@nx-smartmonkey/shared/domain';
import { HSpacer, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';

import CustomFieldIcon from '../../../../../../../assets/svg/CustomFieldsIcon';
import { DriverProjectFieldElement } from '../../../../../../../domain/driversProject/DriversProject';
import Button from '../../../../../../../react-components/Button';
import InputText from '../../../../../../../react-components/InputText';
import { Text } from '../../../../../../../react-components/Text';
import { MultipleChoiceForm } from './MultipleChoiceForm';
import { StopReportContainer } from './StopReportContainer';

interface ReportFieldsProps {
  fieldElement: DriverProjectFieldElement;
  position: number;
  addData: (key: string, value: any) => void;
  customFields?: CustomFieldsValueObject;
  showIcon?: boolean;
  showMandatory: boolean;
}

export const ReportFields = ({
  fieldElement,
  position,
  addData,
  customFields,
  showIcon = true,
  showMandatory,
}: ReportFieldsProps) => {
  switch (fieldElement.type) {
    case `boolean`:
      return (
        <>
          <StopReportContainer
            key={position}
            text={fieldElement.is_custom_field ? fieldElement.label : undefined}
            textId={!fieldElement.is_custom_field ? fieldElement.label : undefined}
            childrenImage={
              showIcon ? (
                <CustomFieldIcon style={{ color: RoutalPalette.secondary00, width: `20px`, height: `15px` }} />
              ) : null
            }
            showMandatory={showMandatory}
            isMandatory={fieldElement.is_required}
            hasValue={customFields?.value[fieldElement.id] !== undefined}
          >
            <Grid item style={{ display: `flex`, justifyContent: `space-around` }}>
              <Button
                style={{
                  display: `flex`,
                  alignItems: `center`,
                  justifyContent: `center`,
                  width: `100%`,
                  height: `30px`,
                  fontWeight: `normal`,
                  backgroundColor:
                    customFields?.value[fieldElement.id] === false ? RoutalPalette.primary40 : RoutalPalette.white,
                  borderRadius: `10px`,
                  border:
                    customFields?.value[fieldElement.id] === false
                      ? `1px solid ${RoutalPalette.primary40}`
                      : `1px solid ${RoutalPalette.grey7}`,
                }}
                onClick={(event) => addData(fieldElement.id, false)}
              >
                <Text
                  intlId={`no`}
                  variant="label"
                  style={{
                    fontSize: `13px`,
                    textAlign: `center`,
                    color:
                      customFields?.value[fieldElement.id] === false ? RoutalPalette.white : RoutalPalette.secondary00,
                  }}
                />
              </Button>
              <HSpacer medium />
              <Button
                style={{
                  display: `flex`,
                  alignItems: `center`,
                  justifyContent: `center`,
                  width: `100%`,
                  height: `30px`,
                  fontWeight: `normal`,
                  backgroundColor: customFields?.value[fieldElement.id] ? RoutalPalette.primary40 : RoutalPalette.white,
                  borderRadius: `10px`,
                  border: customFields?.value[fieldElement.id]
                    ? `1px solid ${RoutalPalette.primary40}`
                    : `1px solid ${RoutalPalette.grey7}`,
                }}
                onClick={(event) => addData(fieldElement.id, true)}
              >
                <Text
                  intlId={`yes`}
                  variant="label"
                  style={{
                    textAlign: `center`,
                    fontSize: `13px`,
                    color: customFields?.value[fieldElement.id] ? RoutalPalette.white : RoutalPalette.secondary00,
                  }}
                />
              </Button>
            </Grid>
          </StopReportContainer>
          <VSpacer medium />
        </>
      );
    case `select`:
    case `multiselect`:
      return fieldElement.options ? (
        <div key={position}>
          <MultipleChoiceForm
            key={position}
            options={fieldElement.options}
            title={fieldElement.label}
            headerImage={
              showIcon ? (
                <CustomFieldIcon style={{ color: RoutalPalette.secondary00, width: `20px`, height: `15px` }} />
              ) : null
            }
            arrayMultiSelect={
              customFields?.value[fieldElement.id] ? (customFields?.value[fieldElement.id] as number[]) : []
            }
            setArrayMultiSelect={(value) => addData(fieldElement.id, value)}
            multipleChoice={fieldElement.type === `multiselect`}
            showMandatory={showMandatory}
            isMandatory={fieldElement.is_required}
            isCustomField
          />
          <VSpacer medium />
        </div>
      ) : null;
    case `string`:
      return (
        <>
          <StopReportContainer
            key={position}
            text={fieldElement.is_custom_field ? fieldElement.label : undefined}
            textId={!fieldElement.is_custom_field ? fieldElement.label : undefined}
            childrenImage={
              showIcon ? (
                <CustomFieldIcon style={{ color: RoutalPalette.secondary00, width: `20px`, height: `15px` }} />
              ) : null
            }
            showMandatory={showMandatory}
            isMandatory={fieldElement.is_required}
            hasValue={customFields?.value[fieldElement.id] !== undefined}
          >
            <InputText
              multiline
              rowsMin={4}
              fieldValue={customFields?.value[fieldElement.id]?.toString() || ``}
              onChange={(value) => addData(fieldElement.id, value)}
              variant="outlined"
              inputContainerStyles={{ width: `100%` }}
              inputStyles={{
                width: `100%`,
                padding: `16.5px 14px`,
                borderColor: RoutalPalette.grey7,
                color: customFields?.value[fieldElement.id]?.toString() ? RoutalPalette.text : undefined,
              }}
            />
          </StopReportContainer>
          <VSpacer medium />
        </>
      );
    case `number`:
      return (
        <>
          <StopReportContainer
            key={position}
            text={fieldElement.is_custom_field ? fieldElement.label : undefined}
            textId={!fieldElement.is_custom_field ? fieldElement.label : undefined}
            childrenImage={
              showIcon ? (
                <CustomFieldIcon style={{ color: RoutalPalette.secondary00, width: `20px`, height: `15px` }} />
              ) : null
            }
            showMandatory={showMandatory}
            isMandatory={fieldElement.is_required}
            hasValue={customFields?.value[fieldElement.id] !== undefined}
          >
            <InputText
              placeholderIntlId="driver.stop.report.numerical_input_ph"
              fieldValue={customFields?.value[fieldElement.id]?.toString() || ``}
              onChange={(value) => addData(fieldElement.id, value)}
              variant="outlined"
              type="number"
              inputContainerStyles={{ width: `100%` }}
              inputStyles={{
                width: `100%`,
                padding: `16.5px 14px`,
                borderColor: RoutalPalette.grey7,
                color: customFields?.value[fieldElement.id]?.toString() ? RoutalPalette.text : undefined,
              }}
            />
          </StopReportContainer>
          <VSpacer medium />
        </>
      );
    default:
      return null;
  }
};
